import type { ProviderId } from '../types';
import type { ModelOption } from './provider';

export interface ProviderMeta {
  id: ProviderId;
  label: string;
  needsKey: boolean;
  keyPlaceholder: string;
  keyHelpUrl: string | null;
  defaultModel: string;
  /** Shown until the live model list has loaded. */
  staticModels: ModelOption[];
  description: string;
}

export const PROVIDERS: readonly ProviderMeta[] = [
  {
    id: 'anthropic',
    label: 'Anthropic',
    needsKey: true,
    keyPlaceholder: 'sk-ant-...',
    keyHelpUrl: 'https://console.anthropic.com/settings/keys',
    defaultModel: 'claude-opus-5',
    staticModels: [
      { id: 'claude-opus-5', label: 'Claude Opus 5', tools: true },
      { id: 'claude-fable-5-1', label: 'Claude Fable 5.1', tools: true },
      { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', tools: true },
      { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5', tools: true },
    ],
    description: 'Calls api.anthropic.com directly from your browser with your key.',
  },
  {
    id: 'openai',
    label: 'OpenAI',
    needsKey: true,
    keyPlaceholder: 'sk-...',
    keyHelpUrl: 'https://platform.openai.com/api-keys',
    defaultModel: 'gpt-6-sol',
    staticModels: [
      { id: 'gpt-6-sol', label: 'GPT-6 Sol', tools: true },
      { id: 'gpt-6-astra', label: 'GPT-6 Astra', tools: true },
      { id: 'gpt-6-luna', label: 'GPT-6 Luna', tools: true },
    ],
    description: 'Calls api.openai.com directly from your browser with your key.',
  },
  {
    id: 'openrouter',
    label: 'OpenRouter',
    needsKey: true,
    keyPlaceholder: 'sk-or-...',
    keyHelpUrl: 'https://openrouter.ai/keys',
    // Free models come and go, get congested, and some are ruled out by an account's privacy settings.
    // The free router picks one that is up and can call tools, so it is the default that keeps working.
    defaultModel: 'openrouter/free',
    staticModels: [
      { id: 'openrouter/free', label: 'Free router (picks an available free model)', free: true, tools: true },
      { id: 'cohere/north-mini-code:free', label: 'North Mini Code', free: true, tools: true },
      { id: 'nex-agi/nex-n2.5-pro:free', label: 'Nex N2.5 Pro', free: true, tools: true },
      { id: 'anthropic/claude-sonnet-5', label: 'Claude Sonnet 5', tools: true },
      { id: 'anthropic/claude-opus-5', label: 'Claude Opus 5', tools: true },
      { id: 'openai/gpt-6-sol', label: 'GPT-6 Sol', tools: true },
    ],
    description: 'One key for hundreds of models, including free ones that need no credits.',
  },
  {
    id: 'demo',
    label: 'Demo',
    needsKey: false,
    keyPlaceholder: '',
    keyHelpUrl: null,
    defaultModel: 'demo',
    staticModels: [{ id: 'demo', label: 'Scripted demo assistant' }],
    description: 'A scripted assistant that runs in your browser, for trying Cue without a key.',
  },
];

export function providerMeta(id: ProviderId): ProviderMeta {
  return PROVIDERS.find((p) => p.id === id) ?? PROVIDERS[0]!;
}
